// src/app/shared/utils/file-download.util.ts

/**
 * Utility functions for downloading files in the browser
 */

/**
 * Download a JSON object as a file
 * @param data The JSON data to download
 * @param filename The name of the file to download
 * @param pretty Whether to format the JSON with indentation
 */
export function downloadJsonFile(
    data: any,
    filename: string,
    pretty: boolean = true
): void {
    try {
        // Convert data to JSON string
        const jsonString = JSON.stringify(data, null, pretty ? 2 : 0);

        // Create blob with proper MIME type for JSON-LD
        const blob = new Blob([jsonString], {
            type: 'application/ld+json;charset=utf-8'
        });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading file:', error);
        throw new Error('Failed to download file');
    }
}

/**
 * Generate a filename with timestamp
 * @param prefix The prefix for the filename
 * @param extension The file extension (without dot)
 * @returns Formatted filename with timestamp
 */
export function generateTimestampedFilename(
    prefix: string,
    extension: string = 'json'
): string {
    const timestamp = new Date()
        .toISOString()
        .replace(/:/g, '-')
        .replace(/\..+/, ''); // Remove milliseconds

    return `${prefix}_${timestamp}.${extension}`;
}

/**
 * Sanitize a string to be used as a filename
 * Removes or replaces characters that are invalid in filenames
 */
export function sanitizeFilename(filename: string): string {
    return filename
        .replace(/[^a-z0-9_\-\.]/gi, '_') // Replace invalid chars with underscore
        .replace(/_{2,}/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}
